import { ComboBox } from '@/components/shared/ComboBox/ComboBox';
import { IntegrationsService } from '@/services/integrations.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  accessToken?: string;
  postbackUrl?: string;
  initialLists: { id: string; name: string }[];
  initialListsUpdatedAt: number;
  disabled: boolean;
  placeholder: string;
  value: number;
  onChange: (value: number) => void;
};

type ListOption = { value: string; label: string };

export default function ActiveCampaignListSelect({
  accessToken,
  postbackUrl,
  initialLists,
  initialListsUpdatedAt,
  disabled,
  placeholder,
  value,
  onChange,
}: Props) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [selectedOption, setSelectedOption] = useState<ListOption | null>(null);
  const [searchError, setSearchError] = useState<string>();
  const requestVersion = useRef(0);
  const initialSelection = initialLists.find((list) => String(list.id) === String(value));

  const { data: savedList } = useQuery({
    queryKey: ['active-campaign-lists', accessToken, postbackUrl, { list_id: value }],
    queryFn: async () => {
      if (!accessToken || !postbackUrl || !value) return [];
      const response = await IntegrationsService.getActiveCampaignLists({
        access_token: accessToken,
        postback_url: postbackUrl,
        list_id: value,
      });
      return response.data;
    },
    select: (lists) => lists[0],
    enabled: !disabled && !!accessToken && !!postbackUrl && !!value &&
      !initialSelection && selectedOption?.value !== String(value),
    retry: false,
  });

  useEffect(() => () => {
    requestVersion.current += 1;
  }, []);

  const selectedLabel = selectedOption?.value === String(value)
    ? selectedOption.label
    : initialSelection?.name || savedList?.name;

  const fetchOptions = async (query: string): Promise<ListOption[]> => {
    const version = ++requestVersion.current;
    setSearchError(undefined);
    if (disabled || !accessToken || !postbackUrl) return [];

    if (!query.trim()) {
      const options = initialLists.map((list) => ({ value: String(list.id), label: list.name }));
      if (value && selectedLabel && !options.some((option) => option.value === String(value))) {
        options.unshift({ value: String(value), label: selectedLabel });
      }
      return options;
    }

    try {
      const lists = await queryClient.fetchQuery({
        queryKey: ['active-campaign-lists', accessToken, postbackUrl, query.trim()],
        queryFn: async () => {
          const response = await IntegrationsService.getActiveCampaignLists({
            access_token: accessToken,
            postback_url: postbackUrl,
            q: query,
          });
          return response.data;
        },
        retry: false,
      });
      return lists.map((list) => ({ value: String(list.id), label: list.name }));
    } catch {
      if (version === requestVersion.current) {
        setSearchError(t('data_listing_error'));
      }
      return [];
    }
  };

  return (
    <ComboBox
      fetchOptions={fetchOptions}
      queryKeyExtra={['active-campaign-lists', accessToken, postbackUrl, initialListsUpdatedAt, disabled, value, selectedLabel]}
      isEditing={!disabled}
      disabled={disabled}
      value={value ? String(value) : ''}
      returnFullObject
      onChange={(option: ListOption | '') => {
        if (!option) return;
        setSelectedOption(option);
        onChange(Number(option.value));
      }}
      placeholder={placeholder}
      helperText={searchError}
    />
  );
}
